import React from 'react';
import ReactGA from 'react-ga4';
import { useSelector } from 'react-redux';
import { useIntl } from 'react-intl';

import PageBaseProps from 'types/PageBase';
import { ChannelEnum } from 'modules/auth/types/LoginRequest';
import { ErrorTypeEnum, isApiErrorResponse } from 'types/Error';

import { useLoginMFAMutation } from 'modules/auth/service';

import { getContext } from 'modules/dealers/selectors';
import { BreakdownServiceAuthorType, SelfServiceType } from 'modules/selfServices/types/SelfService';
import {
  getConfidentialityPolicyLink,
  getLegalNotice,
  isSelfServiceLoading as isSelfServiceLoadingSelector,
} from 'modules/selfServices/selectors';
import { canCollectAnalyticsData, getLoginData, getMFAData, getSelectedSelfServiceId } from 'modules/auth/selectors';
import { getCommunicationData } from 'modules/communication/selectors';
import { getContactInfoForChannel } from 'modules/communication/utils';

import ResendCodeModal from './ResendCodeModal';
import VerifyCodeForm from './VerifyCodeForm';
import { useDispatch } from '../../hooks';
import { resetLoginData } from 'modules/auth/actions';

const OPEN_RESEND_MODAL_EVENT_CONFIG = {
  category: 'Resend',
  action: 'Modal open',
  label: 'Resend modal has been opened',
};

type VerifyOwnProps = { hideGTC?: boolean, selfServiceId?: string };
type VerifyProps = PageBaseProps & VerifyOwnProps;

const Verify: React.FC<VerifyProps> = ({ onPrev, onNext, hideGTC, selfServiceId }) => {
  const intl = useIntl();
  const dispatch = useDispatch();

  const context = useSelector(getContext);
  const { isDriver } = useSelector(getLoginData);
  const canUseGA = useSelector(canCollectAnalyticsData);
  const confidentialityPolicyLink = useSelector(getConfidentialityPolicyLink);
  const legalNotice = useSelector(getLegalNotice);

  const { selfServicePhoneNumber } = context || {};

  const {
    type,
    connectionId,
    remainingAttempt,
    channel = ChannelEnum.SMS,
  } = useSelector(getMFAData) || {};

  const communicationData = useSelector(getCommunicationData);
  const { customerType } = communicationData;
  const selfServiceIdFromState = useSelector(getSelectedSelfServiceId);
  const isSelfServiceLoading = useSelector(isSelfServiceLoadingSelector);
  const [code, setCode] = React.useState('');
  const [displayModal, setDisplayModal] = React.useState(false);

  const [login, { isLoading: isLoginIn, error, isError }] = useLoginMFAMutation();

  const hasWrongCodeError = isApiErrorResponse(error) && error.data.errorType === ErrorTypeEnum.MFA_WRONG_CODE;
  const inputError = hasWrongCodeError
    ? intl.formatMessage({
      id: 'error.types.MFA_WRONG_CODE',
      defaultMessage: 'Wrong code, please try again',
    })
    : isError;

  const shouldDisplayHelper = Boolean(selfServicePhoneNumber?.local) && hasWrongCodeError;

  const isLoading = isLoginIn || isSelfServiceLoading;
  const isDisabled = code.length < 6 || remainingAttempt === 0;

  const breakdownServiceAuthorType = React.useMemo(() => {
    if (type === SelfServiceType.BREAKDOWN) {
      return isDriver ? BreakdownServiceAuthorType.CUSTOMER : BreakdownServiceAuthorType.BREAKDOWN_MECHANIC;
    }
    return undefined;
  }, [isDriver, type]);

  const handleSendCode = React.useCallback(() => {
    if (canUseGA) {
      ReactGA.event(OPEN_RESEND_MODAL_EVENT_CONFIG);
    }

    setDisplayModal(true);
  }, []);

  const handleBack = React.useCallback(() => {
    dispatch(resetLoginData());
    onPrev?.();
  }, [dispatch, onPrev]);

  const handleNextClick = React.useCallback(async () => {
    try {
      await login({
        connectionId,
        selfServiceId: selfServiceIdFromState ?? selfServiceId,
        code,
        breakdownServiceAuthorType,
      }).unwrap();
      onNext?.();
    } catch {}
  }, [login, connectionId, selfServiceId, code, breakdownServiceAuthorType, onNext]);

  const contactInfo = React.useMemo(()=>
    getContactInfoForChannel(communicationData, channel, customerType),
  [communicationData, channel, customerType]);

  return (
    <>
      <VerifyCodeForm
        code={code}
        onCodeChange={setCode}
        onVerify={handleNextClick}
        onPrev={handleBack}
        onNext={onNext}
        isLoading={isLoading}
        isDisabled={isDisabled}
        inputError={inputError}
        channel={channel}
        contactInfo={contactInfo}
        supportPhoneNumber={selfServicePhoneNumber}
        confidentialityPolicyLink={confidentialityPolicyLink}
        legalNotice={legalNotice}
        hideGTC={hideGTC}
        onResend={handleSendCode}
        shouldDisplayHelper={shouldDisplayHelper}
        canUseGA={canUseGA}
      />
      {displayModal && <ResendCodeModal onClose={setDisplayModal} canUseGA={canUseGA} />}
    </>
  );
};

export default Verify;
